import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { ErrorMessage } from "../components/ErrorMessage";
import { SelectField } from "../components/SelectField";
import { US_STATE_OPTIONS } from "../lib/labels";
import { COUNTRY_OPTIONS, DEFAULT_COUNTRY_ALPHA } from "../lib/countries";
import { requestSignup, SignupApiError } from "../lib/signupApi";

// Public self-signup (Phase 2). A rep or tech joins an org with a join
// code (rep or tech), prefilled from ?code= on an invite link. On a
// successful request the server emails a verification link; we show one
// neutral confirmation whether or not the email was already registered
// (the endpoint's 200 is deliberately blind), so this page never reveals
// account existence.

export function SignUp() {
  const [searchParams] = useSearchParams();
  // A ?code= on the invite link prefills and locks the join code; without
  // it the user types the code their rep gave them.
  const codeFromUrl = searchParams.get("code")?.trim() ?? "";
  const codeLocked = codeFromUrl.length > 0;

  const [code, setCode] = useState(codeFromUrl);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [countryAlpha, setCountryAlpha] = useState<string>(
    DEFAULT_COUNTRY_ALPHA,
  );
  const [stateAlpha, setStateAlpha] = useState<string>("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Holds the server's neutral confirmation message once the request is
  // accepted. Non-null flips the page to the confirmation state.
  const [confirmation, setConfirmation] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (!stateAlpha) {
      setError("Please select your state or province.");
      return;
    }

    setLoading(true);
    try {
      const result = await requestSignup({
        code: code.trim(),
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        countryAlpha,
        stateAlpha,
      });
      // Render the server-provided neutral message verbatim; do not
      // substitute copy that could imply the account did or didn't exist.
      setConfirmation(result.message);
    } catch (err: unknown) {
      // Failures (400 missing/invalid, 429 rate limited) carry a
      // server-authored message; show it as-is.
      if (err instanceof SignupApiError) {
        setError(err.message);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Something went wrong. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg p-4">
      <div className="auth-shell">
        <div className="auth-panel">
          <div className="auth-panel-header px-8 py-6 text-center">
            <h1 className="brand-logo text-2xl">Payment Products</h1>
            <p className="mt-1 text-sm text-brand-muted">Create your account</p>
          </div>

          {confirmation ? (
            <div className="space-y-4 p-8 text-center" role="status">
              <CheckCircle2
                className="mx-auto h-10 w-10 text-emerald-300"
                aria-hidden="true"
              />
              <h2 className="text-lg font-semibold text-brand-text">
                Check your inbox
              </h2>
              <p className="text-sm text-brand-muted">{confirmation}</p>
              <p className="text-sm text-brand-muted">
                Already verified?{" "}
                <Link to="/signin" className="link">
                  Sign in
                </Link>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 p-8">
              {error && <ErrorMessage message={error} />}

              <div>
                <label htmlFor="signup-code" className="label mb-1 block">
                  Join code
                </label>
                <input
                  id="signup-code"
                  name="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="input"
                  placeholder="Enter your join code"
                  required
                  readOnly={codeLocked}
                  aria-readonly={codeLocked || undefined}
                  autoFocus={!codeLocked}
                  maxLength={64}
                />
                {codeLocked && (
                  <p className="mt-1 text-xs text-brand-muted">
                    Prefilled from your invite link.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="signup-email" className="label mb-1 block">
                  Email
                </label>
                <input
                  id="signup-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="you@example.com"
                  required
                  autoFocus={codeLocked}
                  maxLength={320}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="signup-first-name"
                    className="label mb-1 block"
                  >
                    First name
                  </label>
                  <input
                    id="signup-first-name"
                    name="given-name"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="input"
                    required
                    maxLength={64}
                  />
                </div>
                <div>
                  <label
                    htmlFor="signup-last-name"
                    className="label mb-1 block"
                  >
                    Last name
                  </label>
                  <input
                    id="signup-last-name"
                    name="family-name"
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="input"
                    required
                    maxLength={64}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="signup-phone" className="label mb-1 block">
                  Phone
                </label>
                <input
                  id="signup-phone"
                  name="tel"
                  type="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input"
                  maxLength={20}
                />
                <p className="mt-1 text-xs text-brand-muted">
                  Optional. Lets an admin's New Application form fill this in
                  automatically when they pick you as the salesperson.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="signup-country" className="label mb-1 block">
                    Country
                  </label>
                  <SelectField
                    id="signup-country"
                    value={countryAlpha}
                    options={COUNTRY_OPTIONS}
                    onChange={(v) => setCountryAlpha(v)}
                    ariaLabel="Country"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="signup-state" className="label mb-1 block">
                    State / Province
                  </label>
                  <SelectField
                    id="signup-state"
                    value={stateAlpha}
                    options={US_STATE_OPTIONS}
                    onChange={(v) => setStateAlpha(v)}
                    placeholder="Select one..."
                    ariaLabel="State or province"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-2.5"
              >
                {loading ? "Submitting..." : "Create account"}
              </button>

              <p className="text-center text-sm text-brand-muted">
                Already have an account?{" "}
                <Link to="/signin" className="link">
                  Sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
